import { createContext, ReactNode, useContext, useEffect, useMemo, useReducer } from 'react';
import { MAX_STATEMENTS_PER_ACCOUNT, VOA_REASON_CODE_STALE_START } from '../constants';
import { PanelAccount, PanelDocument, PanelStatement } from '../ts/interfaces';
import { isStartDateOver90Days, writeStoredState } from '../utils';

export type ReviewState = {
  documents: PanelDocument[];
  accounts: PanelAccount[];
  applicantId: string;
  applicantName: string;
  // Review-level warning text from status_details (contracts page: FP forwards the
  // error field so the UI can render text, not codes). Display only.
  statusWarnings?: string[];
  hydratedFrom: 'storage' | 'api' | null;
  // Monotonic counter so client keys stay unique across a persist/rehydrate cycle.
  keySeq: number;
};

export const initialReviewState: ReviewState = {
  documents: [],
  accounts: [],
  applicantId: '',
  applicantName: '',
  hydratedFrom: null,
  keySeq: 0
};

export type AccountFields = Partial<
  Pick<
    PanelAccount,
    'institutionName' | 'accountType' | 'accountHolderName' | 'accountNumberLastFour'
  >
>;
export type StatementFields = Partial<
  Pick<PanelAccount['statements'][number], 'startDate' | 'startBalance' | 'endDate' | 'endBalance'>
>;

export type ReviewAction =
  | { type: 'HYDRATE'; payload: Omit<ReviewState, 'keySeq'> & { keySeq?: number } }
  | { type: 'SAVE_ACCOUNT_FIELDS'; payload: { accountKey: string; fields: AccountFields } }
  | {
      type: 'SAVE_STATEMENT_FIELDS';
      payload: { accountKey: string; statementKey: string; fields: StatementFields };
    }
  | { type: 'ADD_STATEMENT'; payload: { accountKey: string; documentId: string } }
  | { type: 'REMOVE_STATEMENT'; payload: { accountKey: string; statementKey: string } }
  | { type: 'ADD_ACCOUNT'; payload: { documentId: string } }
  | { type: 'REMOVE_ACCOUNT'; payload: { accountKey: string; documentId: string } }
  | {
      type: 'MERGE_ACCOUNT_STATEMENTS';
      payload: { targetAccountKey: string; sourceAccountKey: string };
    }
  | { type: 'SPLIT_ACCOUNT_STATEMENTS'; payload: { accountKey: string; documentId: string } }
  | {
      type: 'CLEAR_STATEMENT_REASON_CODE';
      payload: { accountKey: string; statementKey: string; code: number };
    }
  | {
      type: 'SET_STATEMENT_ANALYST_REASON_CODES';
      payload: { accountKey: string; statementKey: string; codes: number[] };
    }
  | { type: 'OVERRIDE_STALE_START_DATE'; payload: { accountKey: string; statementKey: string } };

// The single writer of the derived 90-day code, run at hydrate and on every
// statement save so the code and the raw date never disagree: attached while the
// start date is stale and not overridden, stripped otherwise. An override lasts
// only while the date it excused stays stale; correcting the date to a recent one
// clears it, so a later stale date is flagged again and needs its own Override.
//
// At hydrate a 2023 already on the statement stays even when the client check
// says recent. FVS anchors its window on extraction time and this one on today,
// so the two can disagree by a day; only a date edit or Override may drop a code
// FVS authored.
const withDerivedStaleStart = (
  statement: PanelStatement,
  { keepExisting }: { keepExisting: boolean }
): PanelStatement => {
  const codes = statement.reasonCodes ?? [];
  const hasCode = codes.some((reason) => reason.code === VOA_REASON_CODE_STALE_START);
  const staleDate = isStartDateOver90Days(statement.startDate) || (keepExisting && hasCode);
  const overridden = staleDate && statement.ninetyDayOverridden === true;
  const withoutCode = codes.filter((reason) => reason.code !== VOA_REASON_CODE_STALE_START);
  return {
    ...statement,
    // Omitted rather than false so a cleared flag leaves no trace in stored state.
    ninetyDayOverridden: overridden ? true : undefined,
    reasonCodes:
      staleDate && !overridden
        ? [...withoutCode, { code: VOA_REASON_CODE_STALE_START }]
        : withoutCode
  };
};

const withDerivedStaleStarts = (accounts: PanelAccount[]): PanelAccount[] =>
  accounts.map((account) => ({
    ...account,
    statements: account.statements.map((statement) =>
      withDerivedStaleStart(statement, { keepExisting: true })
    )
  }));

const blankStatement = (key: string, documentId: string): PanelAccount['statements'][number] => ({
  key,
  documentId,
  startDate: '',
  startBalance: '',
  endDate: '',
  endBalance: ''
});

export function assetVerificationReducer(state: ReviewState, action: ReviewAction): ReviewState {
  switch (action.type) {
    case 'HYDRATE': {
      const maxKeySeq = action.payload.keySeq ?? deriveKeySeq(action.payload.accounts);
      return {
        ...state,
        ...action.payload,
        accounts: withDerivedStaleStarts(action.payload.accounts),
        keySeq: maxKeySeq
      };
    }
    case 'SAVE_ACCOUNT_FIELDS':
      return {
        ...state,
        accounts: state.accounts.map((account) =>
          account.key === action.payload.accountKey
            ? {
                ...account,
                ...action.payload.fields,
                // Trim on save so stored state, labels and blank checks never see
                // padding; validation and the wire builder trim for the same reason.
                ...(action.payload.fields.accountNumberLastFour !== undefined && {
                  accountNumberLastFour: action.payload.fields.accountNumberLastFour.trim()
                })
              }
            : account
        )
      };
    case 'SAVE_STATEMENT_FIELDS':
      return {
        ...state,
        accounts: state.accounts.map((account) =>
          account.key === action.payload.accountKey
            ? {
                ...account,
                statements: account.statements.map((statement) =>
                  statement.key === action.payload.statementKey
                    ? withDerivedStaleStart(
                        { ...statement, ...action.payload.fields },
                        { keepExisting: false }
                      )
                    : statement
                )
              }
            : account
        )
      };
    case 'ADD_STATEMENT': {
      const account = state.accounts.find((a) => a.key === action.payload.accountKey);
      if (!account) return state;
      const seq = state.keySeq + 1;
      return {
        ...state,
        keySeq: seq,
        accounts: state.accounts.map((a) =>
          a.key === account.key
            ? {
                ...a,
                statements: [
                  ...a.statements,
                  blankStatement(`stmt-${seq}`, action.payload.documentId)
                ]
              }
            : a
        )
      };
    }
    case 'REMOVE_STATEMENT': {
      const account = state.accounts.find((a) => a.key === action.payload.accountKey);
      // Structural guard: an account never drops to zero statements (submit invariant).
      if (!account || account.statements.length <= 1) return state;
      return {
        ...state,
        accounts: state.accounts.map((a) =>
          a.key === account.key
            ? {
                ...a,
                statements: a.statements.filter((s) => s.key !== action.payload.statementKey)
              }
            : a
        )
      };
    }
    case 'ADD_ACCOUNT': {
      // Deliberately blank — no smart fill (design note 23:6396: a new account may be
      // a whole other institution, and the analyst must transcribe from the document).
      const accountSeq = state.keySeq + 1;
      const statementSeq = state.keySeq + 2;
      return {
        ...state,
        keySeq: statementSeq,
        accounts: [
          ...state.accounts,
          {
            key: `acct-${accountSeq}`,
            institutionName: '',
            accountType: '',
            accountHolderName: '',
            accountNumberLastFour: '',
            statements: [blankStatement(`stmt-${statementSeq}`, action.payload.documentId)]
          }
        ]
      };
    }
    case 'MERGE_ACCOUNT_STATEMENTS': {
      // Reuse tick: the document's blank account IS an earlier account continuing
      // here — fold its statements into that one entity, so the account is submitted
      // once with all its statements (contract §5: grouping travels by identity;
      // splitting one real account per document inflated the total).
      const source = state.accounts.find((a) => a.key === action.payload.sourceAccountKey);
      const target = state.accounts.find((a) => a.key === action.payload.targetAccountKey);
      if (!source || !target || source.key === target.key) return state;
      if (source.statements.length + target.statements.length > MAX_STATEMENTS_PER_ACCOUNT) {
        return state;
      }
      return {
        ...state,
        accounts: state.accounts
          .filter((a) => a.key !== target.key)
          .map((a) =>
            a.key === source.key ? { ...a, statements: [...a.statements, ...target.statements] } : a
          )
      };
    }
    case 'SPLIT_ACCOUNT_STATEMENTS': {
      // Reuse untick: detach this document's statements into a fresh unidentified
      // account — typed statement values survive, only the identity link is undone.
      const account = state.accounts.find((a) => a.key === action.payload.accountKey);
      if (!account) return state;
      const moved = account.statements.filter((s) => s.documentId === action.payload.documentId);
      const kept = account.statements.filter((s) => s.documentId !== action.payload.documentId);
      // Never split an account into an empty shell: with nothing to move (or nothing
      // left behind) the action is a no-op.
      if (moved.length === 0 || kept.length === 0) return state;
      const seq = state.keySeq + 1;
      return {
        ...state,
        keySeq: seq,
        accounts: [
          ...state.accounts.map((a) => (a.key === account.key ? { ...a, statements: kept } : a)),
          {
            key: `acct-${seq}`,
            institutionName: '',
            accountType: '',
            accountHolderName: '',
            accountNumberLastFour: '',
            statements: moved
          }
        ]
      };
    }
    case 'REMOVE_ACCOUNT': {
      const account = state.accounts.find((a) => a.key === action.payload.accountKey);
      if (!account) return state;
      const { documentId } = action.payload;
      const onDocument = (a: PanelAccount) => a.statements.some((s) => s.documentId === documentId);
      // Structural guard: a document never drops to zero accounts.
      if (state.accounts.filter(onDocument).length <= 1) return state;
      // Removing from a document detaches only that document's statements; the
      // account entity survives wherever else it has statements.
      return {
        ...state,
        accounts: state.accounts
          .map((a) =>
            a.key === account.key
              ? { ...a, statements: a.statements.filter((s) => s.documentId !== documentId) }
              : a
          )
          .filter((a) => a.statements.length > 0)
      };
    }
    case 'CLEAR_STATEMENT_REASON_CODE': {
      // The analyst's explicit Resolve action. Field edits touch reasonCodes only
      // through the derived 2023 in SAVE_STATEMENT_FIELDS; every other code changes
      // here or via OVERRIDE_STALE_START_DATE.
      return {
        ...state,
        accounts: state.accounts.map((account) =>
          account.key === action.payload.accountKey
            ? {
                ...account,
                statements: account.statements.map((statement) =>
                  statement.key === action.payload.statementKey
                    ? {
                        ...statement,
                        reasonCodes: (statement.reasonCodes ?? []).filter(
                          (reason) => reason.code !== action.payload.code
                        )
                      }
                    : statement
                )
              }
            : account
        )
      };
    }
    case 'SET_STATEMENT_ANALYST_REASON_CODES': {
      // The dropdown reports the whole selection on every toggle; the case stores
      // it deduped and leaves reasonCodes (FVS's codes) untouched.
      return {
        ...state,
        accounts: state.accounts.map((account) =>
          account.key === action.payload.accountKey
            ? {
                ...account,
                statements: account.statements.map((statement) =>
                  statement.key === action.payload.statementKey
                    ? { ...statement, analystReasonCodes: [...new Set(action.payload.codes)] }
                    : statement
                )
              }
            : account
        )
      };
    }
    case 'OVERRIDE_STALE_START_DATE': {
      return {
        ...state,
        accounts: state.accounts.map((account) =>
          account.key === action.payload.accountKey
            ? {
                ...account,
                statements: account.statements.map((statement) =>
                  statement.key === action.payload.statementKey
                    ? {
                        ...statement,
                        ninetyDayOverridden: true,
                        reasonCodes: (statement.reasonCodes ?? []).filter(
                          (reason) => reason.code !== VOA_REASON_CODE_STALE_START
                        )
                      }
                    : statement
                )
              }
            : account
        )
      };
    }
    default:
      return state;
  }
}

// Highest numeric suffix among persisted keys, so rehydrated states keep minting unique keys.
export const deriveKeySeq = (accounts: PanelAccount[]): number => {
  let max = 0;
  accounts.forEach((account) => {
    [account.key, ...account.statements.map((s) => s.key)].forEach((key) => {
      const n = Number(key.split('-').pop());
      if (Number.isFinite(n) && n > max) max = n;
    });
  });
  return max;
};

type ContextValue = {
  state: ReviewState;
  dispatch: React.Dispatch<ReviewAction>;
};

const AssetVerificationReviewContext = createContext<ContextValue | null>(null);

type ProviderProps = {
  storageKey: string;
  children: ReactNode;
};

export const AssetVerificationReviewProvider = ({ storageKey, children }: ProviderProps) => {
  const [state, dispatch] = useReducer(assetVerificationReducer, initialReviewState);

  // One write per action (effect keyed on state), never per keystroke; nothing
  // is persisted until the first HYDRATE has populated the state.
  useEffect(() => {
    if (state.hydratedFrom === null) return;
    writeStoredState(storageKey, state);
  }, [state, storageKey]);

  const value = useMemo(() => ({ state, dispatch }), [state]);

  return (
    <AssetVerificationReviewContext.Provider value={value}>
      {children}
    </AssetVerificationReviewContext.Provider>
  );
};

export const useAssetVerificationReview = (): ContextValue => {
  const context = useContext(AssetVerificationReviewContext);
  if (!context) {
    throw new Error(
      'useAssetVerificationReview must be used within AssetVerificationReviewProvider'
    );
  }
  return context;
};
